import { Event } from '../../features/events/types/Event';
import { User } from "../api-objects/User";
import { BASE_URL, ENVIRONMENT, requestOptionsGET } from "../Constants";
import { Environment } from '../enums/Environment';
import { ErrorWithMessageCallback, GenericSuccessCallback, UpdateUserDataSuccessCallback } from "./callback-helpers";

// Event tracking toggle - set to false to disable all event tracking
const EVENT_TRACKING_ENABLED = process.env.REACT_APP_EVENT_TRACKING_ENABLED !== 'false';

export async function getUserInfoApi(
    userId: string,
    idToken: string,
): Promise<User> {
    try {
        const response = await fetch(`${BASE_URL}/api/users/${userId}`, requestOptionsGET(idToken))
        if (response.ok) {
            const user: User = await response.json()
            return user
        } else if (response.status === 401) {
            throw new Error("Session expired");
        } else if (response.status === 404) {
            throw new Error(`Nessun utente trovato con id: ${userId}`);
        } else {
            throw new Error("Errore nel recuperare le info dell'utente");
        }
    } catch (error) {
        if (error instanceof Error) {
            console.error("Error fetching user info:", error.message);
            throw error;
        }
        throw new Error("An unexpected error occurred");
    }
}

export async function updateUserInfoWithSwabcode(
    userId: string,
    idToken: string,
    swabcode: string,
    onSuccess: UpdateUserDataSuccessCallback,
    onError: ErrorWithMessageCallback,
) {
    try {
        const response = await fetch(`${BASE_URL}/api/users/${userId}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': idToken,
            },
            body: JSON.stringify
                ({
                    swabcode,
                })
        })
        if (response.ok) {
            const user: User = await response.json()
            onSuccess(user)
        } else {
            onError("Codice non valido")
        }
    } catch (error) {
        console.error("Update user info failed:", error);
        return { success: false, error: "An unexpected error occurred" };
    }
}

export async function updateUserInfoWithBloodcode(
    userId: string,
    idToken: string,
    bloodcodes: string[],
    onSuccess: UpdateUserDataSuccessCallback,
    onError: ErrorWithMessageCallback,
) {
    try {
        const response = await fetch(`${BASE_URL}/api/users/${userId}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': idToken,
            },
            body: JSON.stringify
                ({
                    bloodcodes,
                })
        })
        if (response.ok) {
            const user: User = await response.json()
            onSuccess(user)
        } else {
            onError("Codice non valido")
        }
    } catch (error) {
        console.error("Update user info failed:", error);
        return { success: false, error: "An unexpected error occurred" };
    }
}

export async function updateUserInfo(
    userId: string,
    idToken: string,
    username: string,
    familyName: string,
    phoneNumber: string,
    taxCode: string,
    onSuccess: UpdateUserDataSuccessCallback,
    onError: ErrorWithMessageCallback,
) {
    try {
        const response = await fetch(`${BASE_URL}/api/users/${userId}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': idToken,
            },
            body: JSON.stringify(
                {
                    tax_number: taxCode,
                    data: {
                        name: username,
                        family_name: familyName,
                        mobile_phone: phoneNumber,
                    }
                }
                ,)
        })
        if (response.ok) {
            const user: User = await response.json()
            onSuccess(user)
        } else {
            onError("Errore di aggiornamento dati")
        }
    } catch (error) {
        console.error("Update user info failed:", error);
        return { success: false, error: "An unexpected error occurred" };
    }
}

export async function submitPrivacyConsent(
    userId: string,
    idToken: string,
    version: string,
    d1Consent: boolean,
): Promise<User> {
    const response = await fetch(`${BASE_URL}/api/users/${userId}/consent`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': idToken,
        },
        body: JSON.stringify({ version, d1_consent: d1Consent }),
    })
    if (!response.ok) {
        const detail = await response.text()
        throw new Error(`Errore nel salvataggio del consenso: ${detail || response.statusText}`)
    }
    return await response.json() as User
}

//

export async function saveEvent(
    email: string,
    event_type: string,
    onSuccess: GenericSuccessCallback,
    onError: ErrorWithMessageCallback,
    source_page?: string,
    user_id?: string,
    report_received?: boolean,
    name?: string,
    family_name?: string,
) {
    // Check if event tracking is enabled
    if (!EVENT_TRACKING_ENABLED) {
        console.log('Event tracking disabled:', { email, event_type, source_page, user_id });
        onSuccess({ success: true, message: 'Event tracking disabled' });
        return;
    }

    // DISABLED: Event tracking is temporarily disabled
    // Original API call commented out below
    // console.log('Event tracking disabled:', { email, event_type, source_page, user_id });
    
    // Call success callback immediately without making API call
    onSuccess({ success: true, message: 'Event tracking disabled' });
    
    /* ORIGINAL CODE (DISABLED):
    try {
        const response = await fetch(`${BASE_URL}/api/temp-stats`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(
                {
                    user_id,
                    email,
                    source_page,
                    event_type,
                    report_received,
                    name,
                    family_name,
                }
            ),
        })
        if (response.ok) {
            const success = await response.json()
            onSuccess(success)
        } else {
            // Attempt to parse the response body for error details
            let errorMessage = `Error: ${response.status} ${response.statusText}`;
            try {
                const errorBody = await response.json();
                errorMessage = errorBody?.detail || errorBody?.message || JSON.stringify(errorBody);
            } catch (parseError) {
                console.error("Error parsing response body:", parseError);
            }
            console.error("Create temp stat failed:", errorMessage);
            onError(errorMessage);
        }
    } catch (error) {
        console.error("Create temp stat failed:", error);
        return { success: false, error: "An unexpected error occurred" };
    }
    */
}

export async function createEvent(eventData: Event): Promise<any> {
    // Check if we're in development mode
    if (ENVIRONMENT !== Environment.PRODUCTION) {
        // console.log('Event creation enabled only in production:', eventData);
        // Return a mock success response in development
        return { success: true, message: 'Event creation disabled in development' };
    }

    try {
        const response = await fetch(`${BASE_URL}/api/events`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(eventData),
        })
        if (response.ok) {
            const event = await response.json();
            return event; 
        } else if (response.status === 401) {
            throw new Error("Session expired");
        } else {
            throw new Error("Errore nel creare l'evento");
        }
    } catch (error) {
        if (error instanceof Error)
            if (error.message.includes("Session expired")) {
                throw new Error("Session expired");
            }
        throw new Error("An unexpected error occurred");
    }
}
